/*
 * The IDEAS — first-class widgets for a factory floor and the office above it,
 * one per shape of question, each written against the same sandbox SDK the
 * catalog uses (a module body returning { meta, setup }; scene trees, tokens
 * only, no DOM, no network, no timers).
 *
 * What each one does with its bindings is the point of the exercise, so every
 * entry says so in `about`: which symbols it wants, and what the JS does to them
 * between the wire and the picture — rolling windows, resampling, regression,
 * integration, thresholds. The rule shared by all of them: the widget keeps its
 * own history in its closure across ticks (the worker lives as long as the
 * tile), so a chart, a histogram or an integral needs no backend query — it is
 * built from what the fanout delivered while the tile was on screen.
 *
 * These are seeded onto the "Widget Ideas" dev board; the ones that earn their
 * place graduate into the catalog and the marketplace.
 */
import type { IdeaEntry } from './ideas/types';
import { REFERENCE_IDEAS } from './ideas/reference';
import { FLOOR_IDEAS } from './ideas/floor';
import { CHARTS_IDEAS } from './ideas/charts';
import { SCIENCE_IDEAS } from './ideas/science';

export type { IdeaEntry } from './ideas/types';

/** Ideas that are the app's own components: seeded like the rest, never compiled in the sandbox. */
const NATIVE_IDEAS: IdeaEntry[] = [
  { id: 'notifications', title: 'Notifications', summary: 'Every watched point outside its limits, oldest first, as the app’s own list rows.', configKeys: ['items'], defaults: {}, minW: 3, minH: 2, aspect: '3:2', source: 'native:alarms',
    about: 'Binds N symbols with high/low limits and an optional `margin`. The app’s listRow (the MCP servers / model providers row) per active alarm: label, which limit, the value, how long; an all-clear line otherwise.',
    demo: { config: { items: [{ controllerId: '$ctrl', symbolId: '$cpu', label: 'CPU %', high: 60, margin: 15, unit: '%' }, { controllerId: '$ctrl', symbolId: '$load1', label: 'load 1m', high: 4, margin: 1 }, { controllerId: '$ctrl', symbolId: '$ramAvail', label: 'RAM free', low: 8, margin: 4, unit: 'GB' }, { controllerId: '$ctrl', symbolId: '$core1', label: 'core 1', high: 70, margin: 20, unit: '%' }] }, w: 4, h: 3 } },
];

/** Ideas that graduated: on the marketplace with made-up readings, no longer seeded onto the board. */
const GRADUATED = new Set(['scatter', 'correlation', 'fft', 'deviation', 'stats', 'heatmap', 'boxplot', 'anomaly', 'doughnut', 'pie', 'radar']);

/** Every idea, seeded or graduated: what the marketplace and the harness read. */
export const ALL_IDEAS: IdeaEntry[] = [
  ...NATIVE_IDEAS,
  ...REFERENCE_IDEAS,
  ...FLOOR_IDEAS,
  ...CHARTS_IDEAS,
  ...SCIENCE_IDEAS,
];

/** What the Widget Ideas board shows: the ideas still under review. */
export const IDEAS: IdeaEntry[] = ALL_IDEAS.filter((i) => !GRADUATED.has(i.id));

export function ideaEntry(id: string): IdeaEntry | undefined {
  return ALL_IDEAS.find((i) => i.id === id);
}
